import { useState, type ReactNode } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import LinkIcon from "@mui/icons-material/Link";
import ApartmentIcon from "@mui/icons-material/Apartment";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useAuth0 } from "@auth0/auth0-react";
import type { PaletteMode } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { View } from "../types";

const SIDEBAR_WIDTH = 232;

const titles: Record<View, string> = {
  dashboard: "Dashboard",
  drivers: "Drivers",
  driver: "Driver Detail",
  tenants: "Tenants",
  tenant: "Tenant Detail",
  memberships: "Memberships",
  companies: "Companies",
  api_keys: "API Keys",
  settings: "Settings",
};

interface NavItem {
  view: View;
  label: string;
  icon: ReactNode;
}

const items: NavItem[] = [
  { view: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
  { view: "drivers", label: "Drivers", icon: <PeopleIcon /> },
  { view: "tenants", label: "Tenants", icon: <BusinessIcon /> },
  { view: "memberships", label: "Memberships", icon: <LinkIcon /> },
  { view: "companies", label: "Companies", icon: <ApartmentIcon /> },
  { view: "api_keys", label: "API Keys", icon: <VpnKeyIcon /> },
  { view: "settings", label: "Settings", icon: <SettingsIcon /> },
];

interface Props {
  view: View;
  onNavigate: (v: View, id?: number) => void;
  themeMode: PaletteMode;
  onToggleThemeMode: () => void;
  children: ReactNode;
}

export default function Layout({ view, onNavigate, themeMode, onToggleThemeMode, children }: Props) {
  const { user, logout } = useAuth0();
  const theme = useTheme();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const navItemSx = {
    color: "rgba(255,255,255,0.85)",
    borderRadius: 2,
    mx: 1.25,
    "&.Mui-selected": {
      bgcolor: alpha(theme.palette.primary.main, 0.24),
      color: "#fff",
    },
    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
    "& .MuiListItemIcon-root": { color: "inherit", minWidth: 36 },
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: theme.palette.primary.dark,
          backgroundImage:
            "linear-gradient(180deg, rgba(47,128,255,0.12) 0%, rgba(11,31,58,1) 35%)",
          color: "#fff",
          position: "fixed",
          top: 0,
          left: isSidebarVisible ? 0 : `-${SIDEBAR_WIDTH}px`,
          bottom: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          transition: theme.transitions.create("left", {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Toolbar sx={{ px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Zennet Admin
          </Typography>
        </Toolbar>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <Box sx={{ flexGrow: 1 }}>
          <List dense>
            {items.map((i) => (
              <ListItemButton
                key={i.view}
                selected={view === i.view}
                onClick={() => onNavigate(i.view)}
                sx={navItemSx}
              >
                <ListItemIcon>{i.icon}</ListItemIcon>
                <ListItemText primary={i.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* User profile + logout */}
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={user?.picture}
            alt={user?.name}
            sx={{ width: 32, height: 32, fontSize: 14 }}
          >
            {user?.name?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
            <Typography variant="body2" noWrap sx={{ color: "#fff", fontWeight: 500, lineHeight: 1.2 }}>
              {user?.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {user?.email}
            </Typography>
          </Box>
          <Tooltip title="Sign out">
            <LogoutIcon
              fontSize="small"
              sx={{ color: "rgba(255,255,255,0.5)", cursor: "pointer", "&:hover": { color: "#fff" } }}
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: isSidebarVisible ? `${SIDEBAR_WIDTH}px` : 0,
          p: 4,
          bgcolor: "background.default",
          transition: theme.transitions.create("margin-left", {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={isSidebarVisible ? "Hide side panel" : "Show side panel"}>
              <IconButton
                size="small"
                onClick={() => setIsSidebarVisible((visible) => !visible)}
                sx={{ color: "text.primary" }}
              >
                {isSidebarVisible ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            </Tooltip>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
              {titles[view]}
            </Typography>
          </Box>
          <Tooltip title={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton
              size="small"
              onClick={onToggleThemeMode}
              sx={{ color: theme.palette.mode === "dark" ? "warning.main" : "text.primary" }}
            >
              {themeMode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        {children}
      </Box>
    </Box>
  );
}
